/**
 * Перенос слов по слогам для русского текста — без словаря, правилами слогораздела, одинаково
 * с Rust-зеркалом (engine/psd-render/core/src/hyphenation.rs): холст и печать переносят в одном месте.
 *
 * Между двумя гласными стоит группа согласных; перенос ставится перед ПОСЛЕДНЕЙ согласной группы
 * («памят-ного», «началь-ной», «вырос-ли»), перед одиночной согласной — после гласной
 * («несмышлё-ными», «ро-за»). «й», «ь», «ъ» не отрываются от предыдущей буквы и не начинают строку
 * («май-ка», «подъ-езд», «боль-ше»); «ы» строку тоже не начинает. Две гласные подряд делятся
 * между собой («ма-як»). Ограничения Photoshop поверх: длина слова, буквы до и после переноса.
 */

import { englishHyphenationPoints } from './hyphenate-en';

export interface HyphenationRules {
  /** Слово короче (в буквах) не переносится — PS «Words longer than». */
  minWordLength: number;
  /** Минимум букв до дефиса — PS «After first». */
  minPrefix: number;
  /** Минимум букв после переноса — PS «Before last». */
  minSuffix: number;
  /** Максимум подряд идущих строк с переносом; 0 — без предела — PS «Hyphen limit». */
  maxConsecutive: number;
}

/** Умолчания Photoshop для абзаца с включённым переносом. */
export const DEFAULT_HYPHENATION_RULES: HyphenationRules = {
  minWordLength: 6,
  minPrefix: 2,
  minSuffix: 2,
  maxConsecutive: 2,
};

/** Дефис, который раскладка дописывает в конец перенесённой строки; в исходном тексте его нет. */
export const HYPHEN = '-';

const VOWELS = 'аеёиоуыэюя';
const GLUE = 'йьъ';
const LETTER = /^[а-яё]$/;

/**
 * Позиции допустимых переносов: индекс буквы, с которой начинается следующая строка. Пусто для
 * не-кириллицы, коротких слов и слов, где все точки режут ограничения.
 */
export function hyphenationPoints(word: string, rules: HyphenationRules = DEFAULT_HYPHENATION_RULES): number[] {
  const letters = [...word.toLowerCase()];
  if (letters.length < Math.max(rules.minWordLength, 2) || letters.some(ch => !LETTER.test(ch))) return [];

  const vowels: number[] = [];
  letters.forEach((ch, i) => {
    if (VOWELS.includes(ch)) vowels.push(i);
  });

  const points: number[] = [];
  for (let k = 0; k + 1 < vowels.length; k++) {
    const i = vowels[k];
    const j = vowels[k + 1];
    let p: number;
    if (j === i + 1) {
      if (letters[j] === 'ы') continue;
      p = j;
    } else {
      // Перед последней согласной группы; «й/ь/ъ» перед гласной уходят вместе с группой.
      p = GLUE.includes(letters[j - 1]) ? j : j - 1;
    }

    if (p >= rules.minPrefix && letters.length - p >= rules.minSuffix) points.push(p);
  }

  return points;
}

/** Слово, стоящее в сегменте переноса: ведущие и хвостовые не-буквы остаются на своих местах. */
const WORD_IN_SEGMENT = /^([^\p{L}]*)(\p{L}+)(.*)$/su;
/** Цифра вплотную к букве: «школами2024» — одно слово с цифрами, оно не переносится. */
const DIGIT_AT_END = /\p{N}$/u;
const DIGIT_AT_START = /^\p{N}/u;

export interface HyphenSplit {
  /** Начало сегмента с дописанным дефисом — то, что остаётся на текущей строке. */
  head: string;
  /** Остаток сегмента — начало следующей строки. */
  rest: string;
}

/**
 * Самый длинный кусок `segment`, который вместе с `prefix` и дефисом влезает в `fits`.
 * `undefined` — слово не переносится или ни одна точка не влезает.
 */
export function splitSegmentAtHyphen(
  prefix: string,
  segment: string,
  rules: HyphenationRules,
  fits: (candidate: string) => boolean,
): HyphenSplit | undefined {
  return splitSegmentWith(prefix, segment, word => hyphenationPoints(word, rules), fits);
}

/** {@link splitSegmentAtHyphen} with the word's points from `points(word, offset of the word in segment)`. */
export function splitSegmentWith(
  prefix: string,
  segment: string,
  points: (word: string, offset: number) => number[],
  fits: (candidate: string) => boolean,
): HyphenSplit | undefined {
  const match = WORD_IN_SEGMENT.exec(segment);
  if (!match) return undefined;
  const [, lead, word, tail] = match;
  if (DIGIT_AT_END.test(lead) || DIGIT_AT_START.test(tail)) return undefined;
  const chars = [...word];
  const wordPoints = points(word, lead.length);
  for (let n = wordPoints.length - 1; n >= 0; n--) {
    const p = wordPoints[n];
    const head = prefix + lead + chars.slice(0, p).join('') + HYPHEN;
    if (fits(head)) return { head, rest: chars.slice(p).join('') + tail };
  }

  return undefined;
}

/** Photoshop `Language` indices (Adobe text-engine order) we hyphenate by. */
export const PS_LANGUAGE_ENGLISH_USA = 0;
export const PS_LANGUAGE_RUSSIAN = 18;

/** The language each char of a box text hyphenates by: its run's `language`, else the box's. Rust twin `WordLanguages`. */
export class WordLanguages {
  private readonly languages: number[];

  constructor(
    private readonly text: string,
    boxLanguage: number,
    runs: readonly { from: number; to: number; language?: number }[] = [],
  ) {
    this.languages = new Array<number>(text.length).fill(boxLanguage);
    for (const r of runs) if (r.language != null) this.languages.fill(r.language, r.from, Math.min(r.to, text.length));
  }

  /** Points of `word` whose first char is char `start`; a fragment after an earlier break takes its whole word's points. */
  points(word: string, start: number, rules: HyphenationRules): number[] {
    switch (this.languages[start]) {
      case PS_LANGUAGE_RUSSIAN:
        return hyphenationPoints(word, rules);
      case PS_LANGUAGE_ENGLISH_USA: {
        let wordStart = start;
        while (wordStart > 0 && LETTER_ANY.test(this.text[wordStart - 1])) wordStart--;
        const skip = start - wordStart;
        const whole = this.text.slice(wordStart, start + word.length);
        return englishHyphenationPoints(whole, rules)
          .filter(p => p > skip)
          .map(p => p - skip);
      }

      default:
        return [];
    }
  }
}

const LETTER_ANY = /^\p{L}$/u;
